import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./components/Pages/Home";
import About from "./components/Pages/About";
import Doctors from "./components/Pages/Doctors";
import Appointments from "./components/Pages/Appointments";
import Contact from "./components/Pages/Contact";
import Career from "./components/Pages/Career";
import ErrorPage from "./components/Pages/ErrorPage";
import DietaryForm from "./components/user/healthseeker/dietaryReport/page";
import SuspenseLoader from "./components/ui/common/SuspenseLoader";
import AuthenticationView from "./components/auth";
import { usePageTracking } from "./hooks/usePageTracking";
import DashboardLayout from "./components/ui/desktop/DashboardLayout.jsx";
import InterviewForm from "./components/Pages/InterviewForm.jsx";
const ExplorePlans = lazy(() => import("./components/user/ExplorePlans.jsx"));
const G = lazy(() => import("./components/Pages/G"));
const Ourteam = lazy(() => import("./components/Pages/OurTeam"));
const ResetPassword = lazy(() => import("./components/auth/resetpassword"));
const ForgotPassword = lazy(() => import("./components/auth/forgotpassword"));
const PrivacyPolicies = lazy(() =>
  import("./components/Pages/privacy-policies")
);
const LatestUpdate = lazy(() => import("./components/Pages/LatestUpdate"))
const Terms = lazy(() => import("./components/Pages/Terms"));
const DashboardView = lazy(() => import("./components/user"));
const Counselling = lazy(() =>
  import("./components/user/counselling.jsx")
);
const AccountSettings = lazy(() => import("./components/user/AccountSettings"));
const HealthReport = lazy(() =>
  import("./components/user/healthseeker/healthReport/health-report")
);
const PaymentSuccess = lazy(() =>
  import("./components/user/healthseeker/sessions/paid/payment-success")
);
const PaymentFailed = lazy(() =>
  import("./components/user/healthseeker/sessions/paid/payment-failed")
);
const MembershipPayment = lazy(() =>
  import("./components/user/membership-payment.jsx")
);
const Notification = lazy(() => import("./components/user/Notification"));
const OtherTerms = lazy(() => import("./components/Pages/OtherTerms"));
const MySubscriptions = lazy(() => import("./components/user/MySubscriptions"));

const authRoutesConfig = [
  { path: "/signup", element: <AuthenticationView /> },
  { path: "/reset-password", element: <Suspense fallback={<SuspenseLoader/>}><ResetPassword /></Suspense> },
  { path: "/forgot-password", element: <Suspense fallback={<SuspenseLoader/>}><ForgotPassword /></Suspense> },
];

const publicRoutesConfig = [
  { path: "about", element: <About /> },
  { path: "doctors", element: <Doctors /> },
  { path: "register", element: <Appointments /> },
  { path: "contact", element: <Contact /> },
  { path: "career", element: <Career /> },
  { path: "ourteam", element: <Ourteam /> },
  { path: "gallery", element: <G /> },
  { path: "privacy-policies", element: <PrivacyPolicies /> },
  { path: "terms", element: <Terms /> },
  { path: "other-terms", element: <OtherTerms /> },
  { path: "Dietary", element: <DietaryForm/>},
  { path: "Latest-update", element: <LatestUpdate /> },
  { path: "career/interview", element: <InterviewForm /> },
  { path: "*", element: <ErrorPage /> },
];

const wrapInSuspense = (element) => (
  <Suspense fallback={<SuspenseLoader />}>{element}</Suspense>
);

// Recursively wrap all routes in Suspense
const wrapRoutesWithSuspense = (routes) => {
  return routes.map((route) => {
    const wrappedRoute = {
      ...route,
      element: wrapInSuspense(route.element),
    };
    if (route.children) {
      wrappedRoute.children = wrapRoutesWithSuspense(route.children);
    }
    return wrappedRoute;
  });
};

const protectedRoutesConfig = [
  { path: "/home", element: <DashboardView /> },
  { path: "/plans", element: <ExplorePlans /> },
  { path: "/account-settings", element: <AccountSettings /> },
  { path: "/notification", element: <Notification /> },
  { path: "/my-subscriptions", element: <MySubscriptions /> },
  { path: "/membership/payment/:planId", element: <MembershipPayment /> },
  { path: "/counselling", element: <Counselling /> },
  { path: "/health-report", element: <HealthReport /> },
  { path: "/payment-success", element: <PaymentSuccess /> },
  { path: "/payment-failed", element: <PaymentFailed /> },
];

export const protectedRoutesWithSuspense = wrapRoutesWithSuspense(
  protectedRoutesConfig
);

const PublicRoutes = ({ isAuthenticated }) => {
  return (
    <Route path="/" element={<Layout />}>
      <Route
        index
        element={
          isAuthenticated ? <Navigate to="/home" replace /> : <Home />
        }
      />
      {publicRoutesConfig.map(({ path, element }, index) => (
        <Route
          key={index}
          path={path}
          element={
            <Suspense fallback={<SuspenseLoader/>}>{element}</Suspense>
          }
        />
      ))}
    </Route>
  );
};

const AuthRoutes = () => {
  return (
    <>
      {authRoutesConfig.map(({ path, element }, index) => (
        <Route key={index} path={path} element={element} />
      ))}
    </>
  );
};

export const RouteManager = () => {

  usePageTracking();

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  
  const accessToken = localStorage.getItem("token");
  const isAuthenticated = !!accessToken;

    return (
      <Suspense fallback={<SuspenseLoader />}>
        <Routes>
          {isAuthenticated && (
            <Route element={<DashboardLayout />}>
              {protectedRoutesWithSuspense.map(({ path, element }, index) => (
                <Route key={index} path={path} element={element} />
              ))}
            </Route>
          )}

          {PublicRoutes({ isAuthenticated })}

          {AuthRoutes()}

          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? "/home" : "/"} replace />}
          />
        </Routes>
      </Suspense>
    );
};
